import { useMemo, useRef, useState, type DragEvent } from "react";
import { toast } from "sonner";
import { OfferCatalogDialog } from "@/components/offer/OfferCatalogDialog";
import { useOfferLineTemplates, type Offer } from "@/hooks/use-offers";
import { useOfferIdeaCardMutations, useOfferIdeaCards } from "@/hooks/use-offer-idea-cards";
import { useOfferIdeaGroupMutations, useOfferIdeaGroups } from "@/hooks/use-offer-idea-groups";
import { useOfferIdeaImageUrls, useUploadOfferIdeaImage } from "@/hooks/use-offer-idea-images";
import { useCreateLineFromIdea } from "@/hooks/use-create-line-from-idea";
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  CARD_WIDTH,
  cardsUsedInBudget,
  clampToBoard,
  EXCERPT_DRAG_TYPE,
  groupAtPoint,
  moveGroupMembers,
  nextCardPosition,
  parseDroppedExcerpt,
  type ExcerptFields,
} from "@/lib/tilbudsarbeidsrom/idea-board";
import type { DBOfferIdeaCard, DBOfferIdeaGroup, OfferIdeaCardKind } from "@/lib/db-types";
import { IdeaBoardToolbar } from "./IdeaBoardToolbar";
import { IdeaCard } from "./IdeaCard";
import { IdeaGroupFrame } from "./IdeaGroupFrame";
import { IdeaSourcePanel } from "./IdeaSourcePanel";

type Deletion = { kind: "cards"; ids: string[] } | { kind: "group"; id: string };

interface IdeaBoardBranchProps {
  offer: Offer;
  hourlyRate: number;
  readOnly: boolean;
}

/**
 * Grenen Idé (VIB-2980): en tavle med kort og grupper med fri plassering (B7).
 * Plassering lagres per tilbud. Et kort kan bli en budsjettlinje via katalogen.
 * Sletting er myk og kan angres så lenge siden er åpen.
 */
export function IdeaBoardBranch({ offer, hourlyRate, readOnly }: IdeaBoardBranchProps) {
  const { data: cards = [], isLoading } = useOfferIdeaCards(offer.id);
  const { data: groups = [] } = useOfferIdeaGroups(offer.id);
  const cardMut = useOfferIdeaCardMutations(offer.id);
  const groupMut = useOfferIdeaGroupMutations(offer.id);
  const upload = useUploadOfferIdeaImage(offer.id);
  const { data: templates = [] } = useOfferLineTemplates();
  const { lines, createFromCard } = useCreateLineFromIdea(offer.id, hourlyRate);
  const { data: imageUrls = {} } = useOfferIdeaImageUrls(
    cards.map((c) => c.storage_path).filter((p): p is string => !!p),
  );

  const used = useMemo(() => cardsUsedInBudget(lines), [lines]);
  // Sist endrede kort ligger øverst. z-index i stedet for DOM-rekkefølge, så fokus ikke mistes.
  const stack = useMemo(
    () => new Map([...cards].sort((a, b) => a.updated_at.localeCompare(b.updated_at)).map((c, i) => [c.id, i + 1])),
    [cards],
  );
  const boardRef = useRef<HTMLDivElement>(null);
  const [undoStack, setUndoStack] = useState<Deletion[]>([]);
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [catalogCard, setCatalogCard] = useState<DBOfferIdeaCard | null>(null);

  function placeCard(card: DBOfferIdeaCard, x: number, y: number) {
    const pos = clampToBoard(x, y);
    const groupId = groupAtPoint(groups, pos.x + CARD_WIDTH / 2, pos.y + 40);
    cardMut.update.mutate([{ id: card.id, patch: { ...pos, group_id: groupId } }]);
  }

  function moveGroup(group: DBOfferIdeaGroup, dx: number, dy: number) {
    const pos = clampToBoard(group.x + dx, group.y + dy, group.width);
    groupMut.update.mutate({ id: group.id, patch: pos });
    const members = moveGroupMembers(cards, group.id, pos.x - group.x, pos.y - group.y);
    if (members.length > 0) {
      cardMut.update.mutate(members.map(({ id, x, y }) => ({ id, patch: { x, y } })));
    }
  }

  const addCard = (kind: OfferIdeaCardKind) => cardMut.create.mutate({ kind, ...nextCardPosition(cards) });
  const addExcerpt = (excerpt: ExcerptFields, at?: { x: number; y: number }) =>
    cardMut.create.mutate({ ...excerpt, ...(at ? clampToBoard(at.x, at.y) : nextCardPosition(cards)) });

  function addGroup() {
    const el = boardRef.current;
    const pos = clampToBoard((el?.scrollLeft ?? 0) + 24, (el?.scrollTop ?? 0) + 24, 560);
    groupMut.create.mutate({ label: "Ny gruppe", ...pos });
  }

  async function uploadImage(card: DBOfferIdeaCard, file: File) {
    setUploading((s) => new Set(s).add(card.id));
    try {
      const path = await upload.mutateAsync(file);
      cardMut.update.mutate([{ id: card.id, patch: { storage_path: path } }]);
    } catch {
      // Feilen er vist som toast av hooken.
    } finally {
      setUploading((s) => {
        const next = new Set(s);
        next.delete(card.id);
        return next;
      });
    }
  }

  function restore(d: Deletion) {
    if (d.kind === "cards") cardMut.setDeleted.mutate({ ids: d.ids, deleted: false });
    else groupMut.setDeleted.mutate({ id: d.id, deleted: false });
    setUndoStack((s) => s.filter((x) => x !== d));
  }

  function remove(d: Deletion, label: string) {
    if (d.kind === "cards") cardMut.setDeleted.mutate({ ids: d.ids, deleted: true });
    else groupMut.setDeleted.mutate({ id: d.id, deleted: true });
    setUndoStack((s) => [...s, d]);
    toast(`${label} slettet`, { action: { label: "Angre", onClick: () => restore(d) } });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    const excerpt = parseDroppedExcerpt(e.dataTransfer.getData(EXCERPT_DRAG_TYPE));
    const el = boardRef.current;
    if (!excerpt || !el || readOnly) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    addExcerpt(excerpt, {
      x: e.clientX - rect.left + el.scrollLeft - CARD_WIDTH / 2,
      y: e.clientY - rect.top + el.scrollTop - 16,
    });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
      <section className="flex flex-col gap-3 min-w-0" aria-labelledby="ide-heading">
        <div>
          <h2 id="ide-heading" className="font-display text-xl">Idé</h2>
          <p className="text-sm text-muted-foreground">
            Notater, bilder, referanser og åpne spørsmål. Tavlen er intern og vises aldri for kunden.
          </p>
        </div>
        {!readOnly && (
          <IdeaBoardToolbar
            busy={cardMut.create.isPending || groupMut.create.isPending}
            undoCount={undoStack.length}
            onAddCard={addCard}
            onAddGroup={addGroup}
            onUndo={() => undoStack.length && restore(undoStack[undoStack.length - 1])}
          />
        )}
        <div
          ref={boardRef}
          onDragOver={(e) => {
            if (!readOnly && e.dataTransfer.types.includes(EXCERPT_DRAG_TYPE)) e.preventDefault();
          }}
          onDrop={onDrop}
          className="relative h-[70vh] overflow-auto border-[1.5px] border-foreground/30 rounded-md bg-background"
          aria-label="Idé-tavle"
          role="region"
        >
          <div
            className="relative"
            style={{
              width: BOARD_WIDTH,
              height: BOARD_HEIGHT,
              backgroundImage: "radial-gradient(hsl(var(--foreground) / 0.12) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            {!isLoading && cards.length === 0 && groups.length === 0 && (
              <p className="absolute left-6 top-6 text-sm text-muted-foreground max-w-sm">
                Tavlen er tom. Legg til et notat, et bilde eller et spørsmål, eller dra et møtereferat inn fra
                kundekortet.
              </p>
            )}
            {groups.map((g) => (
              <IdeaGroupFrame
                key={g.id}
                group={g}
                readOnly={readOnly}
                onMove={moveGroup}
                onPatch={(patch) => groupMut.update.mutate({ id: g.id, patch })}
                onDelete={() => remove({ kind: "group", id: g.id }, "Gruppen")}
              />
            ))}
            {cards.map((c) => (
              <IdeaCard
                key={c.id}
                card={c}
                imageUrl={c.storage_path ? imageUrls[c.storage_path] : undefined}
                usedInBudget={used.has(c.id)}
                stackOrder={stack.get(c.id) ?? 1}
                readOnly={readOnly}
                uploading={uploading.has(c.id)}
                onMove={(card, dx, dy) => placeCard(card, card.x + dx, card.y + dy)}
                onPatch={(patch) => cardMut.update.mutate([{ id: c.id, patch }])}
                onUpload={(file) => uploadImage(c, file)}
                onDelete={() => remove({ kind: "cards", ids: [c.id] }, "Kortet")}
                onMakeBudgetLine={() => setCatalogCard(c)}
              />
            ))}
          </div>
        </div>
      </section>

      <IdeaSourcePanel customerId={offer.customer_id} readOnly={readOnly} onAdd={(e) => addExcerpt(e)} />

      <OfferCatalogDialog
        open={catalogCard !== null}
        onOpenChange={(open) => !open && setCatalogCard(null)}
        templates={templates}
        onPick={async (template, qty) => {
          if (catalogCard) await createFromCard(catalogCard, template, qty);
        }}
      />
    </div>
  );
}
